// src/services/users/userService.ts

import authService from '../authService';
import apiService, { API_URL } from '../apiUtils';

export interface User {
    id: number;
    name: string;
    email: string;
    username: string;
    role: 'admin' | 'manager' | 'comptable' | 'caissier';
    phone?: string;
    profile?: string;
    status?: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface UserUpdateInput {
    name?: string;
    email?: string;
    username?: string;
    role?: string;
    phone?: string;
    profile?: File | string | null;
    password?: string;
    confirm_password?: string;
    status?: boolean;
}

export interface UserCreateInput {
    name: string;
    email: string;
    username: string;
    password: string;
    confirm_password: string;
    role: string;
    profile?: File | null;
    phone?: string;
    status?: boolean;
}

/**
 * Helper function to decode JWT token
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
const decodeToken = (token: string | null): any => {
    if (!token) return null;

    try {
        // JWT token consists of three parts: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        // Decode the payload (middle part)
        const payload = parts[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

/**
 * Logs the current user's information to the console, including role from token
 */
const logCurrentUserInfo = (): void => {
    const currentUser = authService.getUser();
    const token = authService.getToken();
    const decodedToken = decodeToken(token);

    if (currentUser && currentUser.username) {
        // Get role from user object or token
        let role = currentUser.role;

        // If role is not in user object, try to get from token
        if (!role && decodedToken && decodedToken.role) {
            role = decodedToken.role;
        }

        console.log(`Current user: ${currentUser.username}, Role: ${role || 'Unknown'}`);

        // If roles from user and token are different, log both
        if (currentUser.role && decodedToken && decodedToken.role && currentUser.role !== decodedToken.role) {
            console.log(`Note: Role in user object (${currentUser.role}) differs from role in token (${decodedToken.role})`);
        }
    } else {
        console.log('No authenticated user found');
    }
};

// Execute the log immediately when the service is loaded
logCurrentUserInfo();

const userService = {
    /**
     * Logs the current user's information to the console
     */
    logCurrentUserInfo,

    /**
     * Decode JWT token to extract payload information
     */
    decodeToken,

    /**
     * Get role from token if available
     * @returns Role from token or null if not available
     */
    getRoleFromToken(): string | null {
        const token = authService.getToken();
        const decodedToken = decodeToken(token);

        return decodedToken && decodedToken.role ? decodedToken.role : null;
    },

    // Get all users from API
    async getAllUsers(): Promise<User[]> {
        try {
            // Log current user before making the request
            logCurrentUserInfo();

            const response = await apiService.get('/users');

            // Check if the response has the expected format
            if (response && response.status === 'success' && Array.isArray(response.data)) {
                return response.data.map((user: any) => ({
                    ...user,
                    // Ensure profile path is properly formatted
                    profile: user.profile ? `${API_URL}/storage/${user.profile}` : undefined
                }));
            }

            throw new Error('Unexpected response format from server');
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error; // Re-throw to handle in component
        }
    },

    // Get a specific user by ID
    async getUser(id: number): Promise<User> {
        try {
            // Log current user before making the request
            logCurrentUserInfo();

            const response = await apiService.get(`/users/${id}`);

            if (response && response.status === 'success' && response.data) {
                const user = response.data;
                // Format profile image URL
                if (user.profile && !user.profile.startsWith('http')) {
                    user.profile = `${API_URL}/storage/${user.profile}`;
                }
                return user;
            }

            throw new Error('User not found');
        } catch (error) {
            console.error(`Error fetching user ${id}:`, error);
            throw error;
        }
    },

    // Create a new user
    async createUser(userData: UserCreateInput): Promise<User> {
        try {
            // Log current user before making the request
            logCurrentUserInfo();

            // Create a FormData object for file upload
            const formData = new FormData();

            // Add all required form fields
            formData.append('name', userData.name);
            formData.append('email', userData.email);
            formData.append('username', userData.username);
            formData.append('role', userData.role);
            formData.append('password', userData.password);
            formData.append('confirm_password', userData.confirm_password);

            // Add optional fields only if they are provided
            if (userData.phone) {
                formData.append('phone', userData.phone);
            } else {
                // Explicitly send an empty value to ensure the backend knows it's intentionally empty
                formData.append('phone', '');
            }

            if (userData.status !== undefined) {
                formData.append('status', userData.status ? '1' : '0');
            }

            // Add profile image if provided
            if (userData.profile instanceof File) {
                formData.append('profile', userData.profile);
            } else {
                // Send empty string for profile if not provided
                formData.append('profile', '');
            }

            // Make the POST request with FormData
            const response = await fetch(`${API_URL}/users`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`,
                    'Accept': 'application/json'
                    // Don't set Content-Type for FormData
                },
                body: formData
            });

            // Parse the response
            const responseText = await response.text();
            let responseData;

            try {
                responseData = JSON.parse(responseText);
            } catch (e) {
                throw new Error(`Invalid response format: ${responseText}`);
            }

            if (!response.ok || responseData.status !== 'success') {
                // Handle validation errors
                if (response.status === 422 && responseData.errors) {
                    const errorMessages = Object.entries(responseData.errors)
                        .flat()
                        .map((entry) => {
                            const [field, messages] = entry as [string, string | string[]];
                            return Array.isArray(messages)
                                ? `${field}: ${messages.join(', ')}`
                                : `${field}: ${messages}`;
                        })
                        .join('; ');
                    throw new Error(`Validation failed: ${errorMessages}`);
                }

                throw new Error(responseData.message || 'Failed to create user');
            }

            // Return the created user
            if (responseData.data) {
                const user = responseData.data;
                // Format profile image URL
                if (user.profile && !user.profile.startsWith('http')) {
                    user.profile = `${API_URL}/storage/${user.profile}`;
                }
                return user;
            }

            throw new Error('Unexpected response format');
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    },

    // Update an existing user
    async updateUser(id: number, userData: UserUpdateInput): Promise<User> {
        try {
            // Log current user before making the request
            logCurrentUserInfo();

            // Create a FormData object for file upload
            const formData = new FormData();

            // Add only the fields that are provided (not undefined)
            if (userData.name !== undefined) {
                formData.append('name', userData.name);
            }

            if (userData.email !== undefined) {
                formData.append('email', userData.email);
            }

            if (userData.username !== undefined) {
                formData.append('username', userData.username);
            }

            if (userData.role !== undefined) {
                formData.append('role', userData.role);
            }

            // For phone, explicitly append empty string if undefined or null
            // This ensures the backend knows we want to keep it empty/optional
            if (userData.phone !== undefined) {
                formData.append('phone', userData.phone || '');
            } else {
                formData.append('phone', '');
            }

            if (userData.status !== undefined) {
                formData.append('status', userData.status ? '1' : '0');
            }

            if (userData.password) {
                formData.append('password', userData.password);
            }

            if (userData.confirm_password) {
                formData.append('confirm_password', userData.confirm_password);
            }

            // Add profile image if provided
            if (userData.profile instanceof File) {
                formData.append('profile', userData.profile);
            } else if (userData.profile === null || userData.profile === '') {
                // Explicitly send empty value if user wants to remove profile
                formData.append('profile', '');
            }
            // Otherwise don't send profile field to keep existing profile

            // Make the POST request with FormData (since Laravel uses POST for updates with _method=PUT)
            const response = await fetch(`${API_URL}/users/${id}/update`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`,
                    'Accept': 'application/json'
                    // Don't set Content-Type for FormData
                },
                body: formData
            });

            // Parse the response
            const responseText = await response.text();
            let responseData;

            try {
                responseData = JSON.parse(responseText);
            } catch (e) {
                throw new Error(`Invalid response format: ${responseText}`);
            }

            if (!response.ok || responseData.status !== 'success') {
                // Handle validation errors
                if (response.status === 422 && responseData.errors) {
                    const errorMessages = Object.entries(responseData.errors)
                        .flat()
                        .map((entry) => {
                            const [field, messages] = entry as [string, string | string[]];
                            return Array.isArray(messages)
                                ? `${field}: ${messages.join(', ')}`
                                : `${field}: ${messages}`;
                        })
                        .join('; ');
                    throw new Error(`Validation failed: ${errorMessages}`);
                }

                throw new Error(responseData.message || 'Failed to update user');
            }

            // Return the updated user
            if (responseData.data) {
                const user = responseData.data;
                // Format profile image URL
                if (user.profile && !user.profile.startsWith('http')) {
                    user.profile = `${API_URL}/storage/${user.profile}`;
                }
                return user;
            }

            throw new Error('Unexpected response format');
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    },

    // Delete a user
    async deleteUser(id: number): Promise<void> {
        try {
            // Log current user before making the request
            logCurrentUserInfo();

            const response = await apiService.delete(`/users/${id}`);

            if (!response || response.status !== 'success') {
                throw new Error(response?.message || 'Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    }
};

export default userService;
